export type SwipeDirection = 'up' | 'down' | 'left' | 'right';
export type SwipeHandler = (direction: SwipeDirection) => void;

const MIN_SWIPE_DISTANCE = 20;
const MAX_SWIPE_TIME = 500;

export class TouchController {
  private element: HTMLElement;
  private onSwipe: SwipeHandler;
  private startX = 0;
  private startY = 0;
  private startTime = 0;
  private active = false;

  constructor(element: HTMLElement, onSwipe: SwipeHandler) {
    this.element = element;
    this.onSwipe = onSwipe;
    this.bindEvents();
  }

  private bindEvents() {
    this.element.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.element.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    this.element.addEventListener('touchmove', this.handleTouchMove, { passive: false });

    // Keyboard support for desktop
    document.addEventListener('keydown', this.handleKeyDown);
  }

  destroy() {
    this.element.removeEventListener('touchstart', this.handleTouchStart);
    this.element.removeEventListener('touchend', this.handleTouchEnd);
    this.element.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  private handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.startTime = Date.now();
    this.active = true;
  };

  private handleTouchMove = (e: TouchEvent) => {
    if (this.active) e.preventDefault();
  };

  private handleTouchEnd = (e: TouchEvent) => {
    if (!this.active) return;
    this.active = false;

    const touch = e.changedTouches[0];
    const dx = touch.clientX - this.startX;
    const dy = touch.clientY - this.startY;
    const dt = Date.now() - this.startTime;

    if (dt > MAX_SWIPE_TIME) return;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    if (Math.max(absDx, absDy) < MIN_SWIPE_DISTANCE) return;

    if (absDx > absDy) {
      this.onSwipe(dx > 0 ? 'right' : 'left');
    } else {
      this.onSwipe(dy > 0 ? 'down' : 'up');
    }
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowUp': case 'w': case 'W':
        e.preventDefault(); this.onSwipe('up'); break;
      case 'ArrowDown': case 's': case 'S':
        e.preventDefault(); this.onSwipe('down'); break;
      case 'ArrowLeft': case 'a': case 'A':
        e.preventDefault(); this.onSwipe('left'); break;
      case 'ArrowRight': case 'd': case 'D':
        e.preventDefault(); this.onSwipe('right'); break;
      case 'z': case 'Z':
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          // Undo will be handled by the game component
          document.dispatchEvent(new CustomEvent('sokoban:undo'));
        }
        break;
      case 'r': case 'R':
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('sokoban:reset'));
        break;
    }
  };
}
